import React, { useState, useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Building, ExternalLink } from 'lucide-react';
import { usePermissions } from '@contexts/AuthContext';
import { contractService, userService } from '@services';
import GenericModal from '@components/common/modals/GenericModal';
import GenericForm, { FormField } from '@components/common/forms/GenericForm';
import Button from '@components/common/Button';
import TierBadge from '@components/common/TierBadge';
import { showToast } from '@utils/toast';
import { getApiErrorMessage, getApiFieldErrors } from '@utils/apiError';
import { getScopeLabel } from '@utils/referenceCatalog';
import { previewContractDivision, ownersSpanDivisions } from '@utils/contractDivision';
import { getAssigneeIds, haveSameAssignees } from '@utils/contractAssignees';
import { tierToVariant } from '@utils/badgeMappers';
import { CONTRACT_AMOUNT_LABEL } from '@utils/contractLabels';
import type { CreateContractRequest, Supplier, ApiError, Contract, UpdateContractRequest } from '@app-types';
import SupplierSearchModal from './SupplierSearchModal';
import ContractRolePicker from './ContractRolePicker';
import styles from './ContractFormModal.module.css';

type AssessmentScopeValue = 'Calculate' | 'Include' | 'Exclude';

interface ContractFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  mode: 'create' | 'edit';
  contract?: Contract;
}

function formAssessmentScope(contract?: Contract): AssessmentScopeValue {
  if (!contract) return 'Calculate';
  if (contract.assessment_scope_mode !== 'Manual') return 'Calculate';
  return contract.assessment_scope === 'Exclude' ? 'Exclude' : 'Include';
}

const ContractFormModal: React.FC<ContractFormModalProps> = ({ isOpen, onClose, onSuccess, mode, contract }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { isAdmin, canAccessNotes } = usePermissions();
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [supplierError, setSupplierError] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [ownerIds, setOwnerIds] = useState<number[]>([]);
  const [managerIds, setManagerIds] = useState<number[]>([]);
  const ownerIdsRef = useRef<number[]>([]);
  const managerIdsRef = useRef<number[]>([]);
  ownerIdsRef.current = ownerIds;
  managerIdsRef.current = managerIds;
  const [assessmentScope, setAssessmentScope] = useState<AssessmentScopeValue>('Calculate');
  const wasOpenRef = useRef(false);

  const isEditMode = mode === 'edit';
  const modalTitle = isEditMode ? 'Edit Contract' : 'Create New Contract';
  const submitLabel = isEditMode ? 'Update Contract' : 'Create Contract';

  // Helper function to format date for HTML5 date input (YYYY-MM-DD)
  const formatDateForInput = (dateString: string | undefined): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0];
  };

  // Reset only when the modal transitions from closed → open so Owner/Manager
  // picks (and supplier) are not wiped by a later re-render while it stays open.
  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current;
    wasOpenRef.current = isOpen;
    if (!justOpened) {
      return;
    }

    setFormErrors({});
    setSupplierError(null);

    if (isEditMode && contract) {
      setSelectedSupplier({
        id: contract.supplier_id,
        vendor_id: contract.vendor_id || '',
        name: contract.supplier_name || '',
        tier_id: 0,
        status: 'Active',
        created_at: '',
        updated_at: '',
        tier_name: contract.tier ?? '',
        tier_description: ''
      });
      setAssessmentScope(formAssessmentScope(contract));
      setOwnerIds(getAssigneeIds(contract.contract_owners));
      setManagerIds(getAssigneeIds(contract.contract_managers));
      return;
    }

    setSelectedSupplier(null);
    setOwnerIds([]);
    setManagerIds([]);
    setAssessmentScope('Calculate');
  }, [isOpen, isEditMode, contract]);

  const { data: assigneeUsers = [] } = useQuery({
    queryKey: ['users', 'contract-assignees'],
    queryFn: () => userService.getUsers(),
    enabled: isOpen
  });

  // Fetch contract types for dropdown
  const { data: contractTypes } = useQuery({
    queryKey: ['contract-types'],
    queryFn: () => contractService.getContractTypes()
  });

  // Create contract mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateContractRequest) => contractService.createContract(data),
    onSuccess: (response) => {
      const contractId = response.contract.id;

      showToast.customSuccess(
        (t) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span>Contract created successfully!</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                showToast.dismiss(t.id);
                navigate(`/contracts/${contractId}`);
              }}
              className={styles.toastActionButton}
            >
              View Details
              <ExternalLink size={14} />
            </Button>
          </div>
        ),
        {
          duration: 7000,
          minWidth: '400px'
        }
      );

      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      setSelectedSupplier(null);
      setOwnerIds([]);
      setManagerIds([]);
      onSuccess();
      onClose();
    },
    onError: (error: ApiError) => {
      const message = getApiErrorMessage(error, 'Failed to create contract');
      const fieldErrors = getApiFieldErrors(error);

      if (error?.response?.status === 409) {
        setFormErrors({ contract_number: message });
      } else if (Object.keys(fieldErrors).length > 0) {
        setFormErrors(fieldErrors);
      } else {
        showToast.error(message);
      }
    }
  });

  // Update contract mutation
  const updateMutation = useMutation({
    mutationFn: (data: UpdateContractRequest) => {
      if (!contract) throw new Error('Contract is required for update');
      return contractService.updateContract(contract.id, data);
    },
    onSuccess: () => {
      showToast.success('Contract updated successfully');
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      if (contract) {
        queryClient.invalidateQueries({ queryKey: ['contract', contract.id] });
        queryClient.invalidateQueries({ queryKey: ['audit-history', 'contract', contract.id] });
        queryClient.invalidateQueries({ queryKey: ['notes', 'contract', contract.id] });
      }
      onSuccess();
      onClose();
    },
    onError: (error: ApiError) => {
      const fieldErrors = getApiFieldErrors(error);

      if (Object.keys(fieldErrors).length > 0) {
        setFormErrors(fieldErrors);
        return;
      }

      showToast.error(getApiErrorMessage(error, 'Failed to update contract'));
    }
  });

  const mutation = isEditMode ? updateMutation : createMutation;

  const ownersChanged = isEditMode && !haveSameAssignees(ownerIds, getAssigneeIds(contract?.contract_owners));
  const managersChanged = isEditMode && !haveSameAssignees(managerIds, getAssigneeIds(contract?.contract_managers));
  const showNoteField = isEditMode && canAccessNotes;

  // The Division is never picked here (ADO-1192): the API derives it from the Contract Owners.
  // On edit it stays the contract's current Division until the Owners are changed (ADO-1086).
  const currentDivision = contract?.division_name || contract?.division || null;
  const { division: divisionPreview, recalculatedOnSave } = previewContractDivision(
    assigneeUsers,
    ownerIds,
    currentDivision,
    !isEditMode || ownersChanged
  );
  const ownersInDifferentDivisions = ownersSpanDivisions(assigneeUsers, ownerIds);

  const pickerContractNumber = contract?.contract_number ?? 'New Contract';
  const rolePickers = [
    {
      roleType: 'owner' as const,
      selectedIds: ownerIds,
      currentAssignees: contract?.contract_owners,
      onChange: (ids: number[]) => {
        ownerIdsRef.current = ids;
        setOwnerIds(ids);
      }
    },
    {
      roleType: 'manager' as const,
      selectedIds: managerIds,
      currentAssignees: contract?.contract_managers,
      onChange: (ids: number[]) => {
        managerIdsRef.current = ids;
        setManagerIds(ids);
      }
    }
  ];

  // Form fields configuration
  const formFields: FormField[] = [
    {
      name: 'contract_number',
      label: 'Contract Number',
      type: isEditMode ? 'readonly' : 'text',
      required: true,
      placeholder: isEditMode ? undefined : 'Enter contract number'
    },
    {
      name: 'name',
      label: 'Contract Name',
      type: 'text',
      required: true,
      placeholder: 'Enter contract name'
    },
    ...(isEditMode
      ? [
          {
            name: 'status',
            label: 'Status',
            type: 'readonly' as const,
          },
        ]
      : []),
    {
      name: 'contract_type',
      label: 'Contract Type',
      type: 'select',
      required: true,
      options: [
        { value: '', label: 'Select Contract Type' },
        ...(contractTypes || []).map((type) => ({
          value: type,
          label: type
        }))
      ]
    },
    {
      name: 'assessment_scope',
      label: 'Assessment Scope',
      type: 'select',
      required: true,
      disabled: !isAdmin,
      options: [
        { value: 'Calculate', label: 'Calculate - Use scope rules' },
        { value: 'Include', label: `${getScopeLabel('Include')} - Eligible for assessments` },
        { value: 'Exclude', label: `${getScopeLabel('Exclude')} - Not eligible for assessments` }
      ]
    },
    {
      name: 'start_date',
      label: 'Start Date',
      type: 'date',
      required: true,
      validation: async (value: unknown, formData?: Record<string, unknown>) => {
        if (!value || typeof value !== 'string') return undefined;

        const endDate = formData?.end_date;
        if (endDate && typeof endDate === 'string' && value > endDate) {
          return 'Start date must be before or equal to end date';
        }

        return undefined;
      }
    },
    {
      name: 'end_date',
      label: 'End Date',
      type: 'date',
      validation: async (value: unknown, formData?: Record<string, unknown>) => {
        if (!value || typeof value !== 'string') return undefined;

        const startDate = formData?.start_date;
        if (startDate && typeof startDate === 'string' && value < startDate) {
          return 'End date must be after or equal to start date';
        }

        return undefined;
      }
    },
    {
      name: 'amount',
      label: CONTRACT_AMOUNT_LABEL,
      type: 'text',
      placeholder: 'Contract amount'
    },
    {
      name: 'division_name',
      label: 'Division',
      type: 'readonly',
      placeholder: 'Calculated from Contract Owner'
    },
    {
      name: 'cost_center',
      label: 'Cost Center',
      type: 'text',
      placeholder: 'Enter cost center (optional)'
    },
    {
      name: 'phi_access',
      label: '',
      type: 'checkbox',
      placeholder: 'PHI Access (Protected Health Information)'
    },
    {
      name: 'pii_access',
      label: '',
      type: 'checkbox',
      placeholder: 'PII Access (Personally Identifiable Information)'
    },
    {
      name: 'bci_access',
      label: '',
      type: 'checkbox',
      placeholder: 'BCI Access (Business Confidential Information)'
    },
    // Saved in the same request as the change, and shown in the contract's Notes and History (ADO-1086).
    ...(showNoteField
      ? [
          {
            name: 'note',
            label: 'Note (optional)',
            type: 'text' as const,
            required: false,
            placeholder: 'Optionally record the reason or context for this contract update'
          }
        ]
      : [])
  ];

  const handleSupplierSelect = (supplier: Supplier) => {
    setSelectedSupplier(supplier);
    setShowSupplierModal(false);
    setSupplierError(null);
  };

  const handleFieldChange = (name: string, value: string | string[] | boolean) => {
    if (formErrors[name]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    if (name === 'assessment_scope' && typeof value === 'string') {
      setAssessmentScope(value as AssessmentScopeValue);
    }
  };

  const handleFormSubmit = (formData: Record<string, string | string[] | boolean>) => {
    setFormErrors({});

    if (!selectedSupplier) {
      setSupplierError('Supplier is required');
      return;
    }

    const assessmentScopeToSend = formData.assessment_scope as AssessmentScopeValue;

    if (isEditMode) {
      const updateData: UpdateContractRequest = {
        name: formData.name as string,
        supplier_id: selectedSupplier.id,
        start_date: formData.start_date as string,
        end_date: (formData.end_date as string) || null,
        contract_type: formData.contract_type as string,
        amount: formData.amount ? Number.parseFloat(formData.amount as string) : null,
        cost_center: (formData.cost_center as string) || null,
        pii_access: (formData.pii_access as boolean) || false,
        phi_access: (formData.phi_access as boolean) || false,
        bci_access: (formData.bci_access as boolean) || false
      };
      if (isAdmin) {
        updateData.assessment_scope = assessmentScopeToSend;
      }
      // Sent only when changed, so an unchanged assignment does not re-run the reassign side effects.
      if (ownersChanged) {
        updateData.contract_owners = ownerIdsRef.current;
      }
      if (managersChanged) {
        updateData.contract_managers = managerIdsRef.current;
      }
      const note = typeof formData.note === 'string' ? formData.note.trim() : '';
      if (showNoteField && note) {
        updateData.note = note;
      }
      updateMutation.mutate(updateData);
    } else {
      const contractData: CreateContractRequest = {
        contract_number: formData.contract_number as string,
        name: formData.name as string,
        supplier_id: selectedSupplier.id,
        start_date: formData.start_date as string,
        end_date: (formData.end_date as string) || undefined,
        contract_type: formData.contract_type as string,
        assessment_scope: isAdmin ? assessmentScopeToSend : 'Calculate',
        amount: formData.amount ? Number.parseFloat(formData.amount as string) : undefined,
        cost_center: (formData.cost_center as string) || undefined,
        pii_access: (formData.pii_access as boolean) || false,
        phi_access: (formData.phi_access as boolean) || false,
        bci_access: (formData.bci_access as boolean) || false
      };
      const selectedOwnerIds = ownerIdsRef.current;
      const selectedManagerIds = managerIdsRef.current;
      if (selectedOwnerIds.length > 0) {
        contractData.contract_owners = selectedOwnerIds;
      }
      if (selectedManagerIds.length > 0) {
        contractData.contract_managers = selectedManagerIds;
      }
      createMutation.mutate(contractData);
    }
  };

  if (!isOpen) return null;

  // Get initial data based on mode
  const initialData =
    isEditMode && contract
      ? {
          contract_number: contract.contract_number,
          name: contract.name,
          status: contract.status,
          contract_type: contract.contract_type,
          assessment_scope: assessmentScope,
          start_date: formatDateForInput(contract.start_date),
          end_date: formatDateForInput(contract.end_date),
          amount: contract.amount?.toString() || '',
          cost_center: contract.cost_center || '',
          pii_access: contract.pii_access || false,
          phi_access: contract.phi_access || false,
          bci_access: contract.bci_access || false,
          note: ''
        }
      : {
          status: 'Active',
          assessment_scope: assessmentScope,
          pii_access: false,
          phi_access: false,
          bci_access: false
        };

  return (
    <>
      <GenericModal
        isOpen={isOpen}
        onClose={onClose}
        title={modalTitle}
        size="lg"
        disableBackdropClick={mutation.isPending}
        dataTestId={isEditMode ? 'edit-contract-modal' : 'create-contract-modal'}
      >
        <div className={styles.modalContent}>
          {/* Supplier Selection Section */}
          <div className={styles.supplierSelectionSection}>
            <span className={styles.formLabel}>
              Supplier <span className={styles.required}>*</span>
            </span>
            {selectedSupplier ? (
              <div className={styles.selectedSupplierDisplay}>
                <div className={styles.supplierInfo}>
                  <Building size={16} />
                  <div>
                    <div className={styles.supplierName}>{selectedSupplier.name}</div>
                    {selectedSupplier.vendor_id && (
                      <div className={styles.supplierId}>Supplier #{selectedSupplier.vendor_id}</div>
                    )}
                    <div className={styles.supplierTier}>
                      <TierBadge
                        variant={tierToVariant(selectedSupplier.tier_name)}
                        dataTestId="selected-supplier-tier-badge"
                      >
                        {selectedSupplier.tier_name || '-'}
                      </TierBadge>
                    </div>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowSupplierModal(true)}
                  disabled={mutation.isPending}
                  dataTestId={isEditMode ? 'edit-contract-change-supplier-btn' : 'create-contract-change-supplier-btn'}
                >
                  Change
                </Button>
              </div>
            ) : (
              <Button
                type="button"
                variant="secondary"
                onClick={() => setShowSupplierModal(true)}
                disabled={mutation.isPending}
                dataTestId={isEditMode ? 'edit-contract-select-supplier-btn' : 'create-contract-select-supplier-btn'}
              >
                <Building size={16} />
                Select Supplier
              </Button>
            )}
            {supplierError && <span className={styles.formError}>{supplierError}</span>}
          </div>

          <div className={styles.roleSelectionRow}>
            {rolePickers.map(({ roleType, ...picker }) => (
              <ContractRolePicker
                key={roleType}
                roleType={roleType}
                users={assigneeUsers}
                contractNumber={pickerContractNumber}
                supplierName={selectedSupplier?.name}
                disabled={mutation.isPending}
                {...picker}
              />
            ))}

            {ownersInDifferentDivisions && (
              <p className={styles.roleWarning} role="alert" data-test-id="owners-different-divisions-warning">
                The selected Contract Owners are in different divisions.
                {isEditMode
                  ? ' The contract keeps its current Division and a Division Conflict is raised.'
                  : ' The contract gets no Division and a Division Conflict is raised.'}
              </p>
            )}
            {recalculatedOnSave && (
              <p className={styles.roleHint} data-test-id="division-recalculated-hint">
                The Division will be recalculated from the Contract Owners when you save.
              </p>
            )}
          </div>

          {/* Form Fields */}
          <GenericForm
            key={isEditMode && contract ? `edit-contract-${contract.id}-${isOpen}` : `create-contract-${isOpen}`}
            fields={formFields}
            initialData={initialData}
            onSubmit={handleFormSubmit}
            onCancel={onClose}
            onFieldChange={handleFieldChange}
            isSubmitting={mutation.isPending}
            submitLabel={submitLabel}
            submitDisabled={!selectedSupplier}
            externalErrors={formErrors}
            externalValues={{ assessment_scope: assessmentScope, division_name: divisionPreview ?? '' }}
          />
        </div>
      </GenericModal>

      {/* Supplier Search Modal */}
      <SupplierSearchModal
        isOpen={showSupplierModal}
        onClose={() => setShowSupplierModal(false)}
        onSupplierSelect={handleSupplierSelect}
      />
    </>
  );
};

export default ContractFormModal;
